import * as React from 'react'

import { cn } from '@/utils/common'

export interface InputProps extends Omit<
  React.ComponentProps<'input'>,
  'size'
> {
  leadingIcon?: React.ReactNode
  trailingIcon?: React.ReactNode
  onTrailingIconClick?: () => void
  size?: Size
}
type Size = 'sm' | 'default' | 'lg'
/**
 * Input Component
 *
 * A flexible input component built on shadcn/ui with support for leading and trailing icons.
 * Eliminates the need for wrapper components like SearchInput.
 *
 * Key features:
 * - Support for leading and trailing icons
 * - Clickable trailing icon (e.g., for clear button, password toggle)
 * - Multiple sizes: sm, default, lg
 * - All standard HTML input props
 * - Proper icon spacing and positioning
 * - Accessible with proper ARIA attributes
 *
 * @param leadingIcon - Icon to display at the start of the input
 * @param trailingIcon - Icon to display at the end of the input
 * @param onTrailingIconClick - Click handler for trailing icon
 * @param size - Size variant: sm, default, or lg
 * @param className - Additional CSS classes
 *
 * @example
 * ```tsx
 * // Search input with icon
 * <Input
 *   leadingIcon={<SearchIcon />}
 *   placeholder="Search..."
 *   value={search}
 *   onChange={(e) => setSearch(e.target.value)}
 * />
 *
 * // Password input with toggle
 * <Input
 *   type={showPassword ? 'text' : 'password'}
 *   trailingIcon={showPassword ? <EyeOffIcon /> : <EyeIcon />}
 *   onTrailingIconClick={() => setShowPassword(!showPassword)}
 * />
 * ```
 */
const LeadingIcon: React.FC<{
  icon: React.ReactNode
  size: Size
  iconSizeClasses: Record<string, string>
}> = ({ icon, size, iconSizeClasses }) => (
  <span
    className={cn(
      'absolute inset-y-0 flex items-center justify-center pointer-events-none text-muted-foreground',
      size === 'sm' && 'left-2.5',
      size === 'default' && 'left-3',
      size === 'lg' && 'left-3.5'
    )}
  >
    <span
      className={cn('flex items-center justify-center', iconSizeClasses[size])}
    >
      {icon}
    </span>
  </span>
)

const TrailingIconButton: React.FC<{
  icon: React.ReactNode
  size: Size
  iconSizeClasses: Record<string, string>
  onClick?: () => void
}> = ({ icon, size, iconSizeClasses, onClick }) => (
  <button
    className={cn(
      'absolute inset-y-0 flex items-center justify-center text-muted-foreground bg-transparent border-0',
      size === 'sm' && 'right-2.5',
      size === 'default' && 'right-3',
      size === 'lg' && 'right-3.5',
      onClick ? 'cursor-pointer hover:text-foreground' : 'pointer-events-none'
    )}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={
      onClick
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onClick()
            }
          }
        : undefined
    }
    type="button"
  >
    <span
      className={cn('flex items-center justify-center', iconSizeClasses[size])}
    >
      {icon}
    </span>
  </button>
)

const getSizeClasses = (size: Size) =>
  ({
    sm: 'h-8 text-xs',
    default: 'h-10 text-base md:text-sm',
    lg: 'h-12 text-base',
  })[size]

const getPaddingClasses = (
  hasLeadingIcon: boolean,
  hasTrailingIcon: boolean,
  size: Size
) =>
  cn({
    'pl-8': hasLeadingIcon && size === 'sm',
    'pl-9': hasLeadingIcon && size === 'default',
    'pl-10': hasLeadingIcon && size === 'lg',
    'pr-8': hasTrailingIcon && size === 'sm',
    'pr-9': hasTrailingIcon && size === 'default',
    'pr-10': hasTrailingIcon && size === 'lg',
    'px-3': !hasLeadingIcon && !hasTrailingIcon,
    'pl-3': !hasLeadingIcon && hasTrailingIcon,
    'pr-3': hasLeadingIcon && !hasTrailingIcon,
  })

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      leadingIcon,
      trailingIcon,
      onTrailingIconClick,
      size = 'default',
      ...props
    },
    ref
  ) => {
    const hasLeadingIcon = !!leadingIcon
    const hasTrailingIcon = !!trailingIcon

    const sizeClass = getSizeClasses(size)
    const iconSizeClass = {
      sm: 'w-3.5 h-3.5',
      default: 'w-4 h-4',
      lg: 'w-5 h-5',
    }
    const paddingClass = getPaddingClasses(
      hasLeadingIcon,
      hasTrailingIcon,
      size
    )

    if (hasLeadingIcon || hasTrailingIcon) {
      return (
        <div className="relative w-full">
          {hasLeadingIcon && (
            <LeadingIcon
              icon={leadingIcon}
              size={size}
              iconSizeClasses={iconSizeClass}
            />
          )}
          <input
            type={type}
            className={cn(
              'flex w-full rounded-md border border-solid border-[--Colors-Gray-light-300] bg-background py-2 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-[--Colors-Gray-light-500] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
              sizeClass,
              paddingClass,
              className
            )}
            ref={ref}
            {...props}
          />
          {hasTrailingIcon && (
            <TrailingIconButton
              icon={trailingIcon}
              size={size}
              iconSizeClasses={iconSizeClass}
              onClick={onTrailingIconClick}
            />
          )}
        </div>
      )
    }

    return (
      <input
        type={type}
        className={cn(
          'flex w-full rounded-md border border-solid border-[--Colors-Gray-light-300] bg-background px-3 py-2 ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-[--Colors-Gray-light-500] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring  disabled:cursor-not-allowed disabled:opacity-50',
          sizeClass,
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = 'Input'

export { Input }
